import type { MeasurementPreferences } from '@kangaroo/shared';

export const METRIC_MEASUREMENT_PREFERENCES: MeasurementPreferences = {
  speedUnit: 'KPH',
  temperatureUnit: 'C',
  distanceUnit: 'KM',
};

const IMPERIAL_REGIONS = new Set(['US', 'LR', 'MM']);
const MPH_CELSIUS_REGIONS = new Set(['GB', 'GG', 'IM', 'JE']);

function browserLocale() {
  if (typeof navigator === 'undefined') return 'en-US';
  return navigator.languages?.[0] ?? navigator.language ?? 'en-US';
}

function localeRegion(locale: string) {
  try {
    return new Intl.Locale(locale).region?.toUpperCase() ?? null;
  } catch {
    return locale.split(/[-_]/)[1]?.toUpperCase() ?? null;
  }
}

export function measurementDefaultsForLocale(locale = browserLocale()): MeasurementPreferences {
  const region = localeRegion(locale);
  if (region && IMPERIAL_REGIONS.has(region)) {
    return { speedUnit: 'MPH', temperatureUnit: 'F', distanceUnit: 'MI' };
  }

  if (region && MPH_CELSIUS_REGIONS.has(region)) {
    return { speedUnit: 'MPH', temperatureUnit: 'C', distanceUnit: 'MI' };
  }

  return METRIC_MEASUREMENT_PREFERENCES;
}

export function getMeasurementPreferences(preferences?: MeasurementPreferences | null): MeasurementPreferences {
  return preferences ?? measurementDefaultsForLocale();
}

export function speedUnitLabel(preferences: MeasurementPreferences) {
  return preferences.speedUnit === 'MPH' ? 'mph' : 'km/h';
}

export function temperatureUnitLabel(preferences: MeasurementPreferences) {
  return preferences.temperatureUnit === 'F' ? 'F' : 'C';
}

export function distanceUnitLabel(preferences: MeasurementPreferences) {
  return preferences.distanceUnit === 'MI' ? 'mi' : 'km';
}

export function convertSpeedKph(value: number, preferences: MeasurementPreferences) {
  return preferences.speedUnit === 'MPH' ? value * 0.621371 : value;
}

export function convertTemperatureCelsius(value: number, preferences: MeasurementPreferences) {
  return preferences.temperatureUnit === 'F' ? (value * 9 / 5) + 32 : value;
}

export function convertDistanceKm(value: number, preferences: MeasurementPreferences) {
  return preferences.distanceUnit === 'MI' ? value * 0.621371 : value;
}

export function formatMeasurement(value: number, maximumFractionDigits = 1) {
  return value.toLocaleString(undefined, { maximumFractionDigits });
}

export function formatSpeedKph(value: number, preferences: MeasurementPreferences) {
  return formatMeasurement(convertSpeedKph(value, preferences), 1);
}

export function formatTemperatureCelsius(value: number, preferences: MeasurementPreferences) {
  return formatMeasurement(convertTemperatureCelsius(value, preferences), 1);
}